/**
 * Tour launch executor — runs a flow's `launch` actions to mount the surface
 * that hosts it (wizard modal, designer, work-item detail modal, ...) before
 * the flow's scoped provider is asked to open the tour.
 *
 * Actions run sequentially:
 *  - `nav`: merge a query string into the current URL and navigate to it.
 *    Values starting with '/' are treated as a full path+query instead —
 *    a client navigation to that route (e.g.
 *    '/client-zone/dashboard?view=smartfield'). When the provider supplies a
 *    `navigate` callback (App Router `router.push`), it is used for both
 *    forms — raw `history.pushState` changes the URL bar but does NOT
 *    propagate to `useSearchParams` on statically rendered pages, so the
 *    host surfaces never saw `?ai_form_wizard=true`/`?tab=builder` open.
 *    Without a router the fallback is pushState + a `popstate` nudge, which
 *    the App Router's popstate listener does pick up for same-page query
 *    changes.
 *  - `click`: DOM-click `[data-tour-id="<value>"]`, or a raw CSS selector when
 *    the value starts with '.', '#' or '['.
 *  - `wait_for`: poll until the target exists (same selector rules) —
 *    confirms the surface actually mounted before the next action runs.
 *  - `process_action`: fire a `requestProcessAction` intent — the host tab's
 *    management component opens its wizard, or consumes the pending intent
 *    as soon as it mounts after the flow's target_tab navigation.
 *
 * Every step is best-effort: failures are swallowed so a missing button or
 * denied surface degrades to the pre-launch behaviour instead of throwing.
 */

import type { TourLaunchAction } from '@/config/guided-tours/types';
import { requestProcessAction } from '@/lib/processActions';

const POST_NAV_SETTLE_MS = 500;
const POST_CLICK_SETTLE_MS = 350;
const WAIT_FOR_TIMEOUT_MS = 12000;
const WAIT_FOR_POLL_MS = 250;

function toSelector(target: string): string {
  const trimmed = target.trim();
  return trimmed.startsWith('.') || trimmed.startsWith('#') || trimmed.startsWith('[')
    ? trimmed
    : `[data-tour-id="${trimmed}"]`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function waitForElement(selector: string, timeoutMs: number): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (document.querySelector(selector)) return true;
    await sleep(WAIT_FOR_POLL_MS);
  }
  return Boolean(document.querySelector(selector));
}

export interface TourLaunchOptions {
  /**
   * Client-side navigation — the provider passes App Router `router.push`.
   * Required for cross-route navs (path starting with '/') and preferred for
   * query merges; the pushState fallback only works for same-page query
   * changes.
   */
  navigate?: (href: string) => void;
  /**
   * Module key of the flow being launched. Scoped onto `process_action`
   * intents so only the shared component hosting this module's surface
   * consumes them — other mounted instances of the same component on
   * different pages (e.g. a personal embed on the dashboard) ignore it.
   */
  moduleKey?: string;
}

export async function runTourLaunch(
  actions: TourLaunchAction[],
  options?: TourLaunchOptions,
): Promise<void> {
  if (typeof window === 'undefined') return;

  for (const action of actions) {
    try {
      if (action.nav) {
        const url = new URL(window.location.href);
        if (action.nav.startsWith('/')) {
          // Full path+query — a soft navigation to another route (e.g. the
          // SmartTask flows hosted on /client-zone/dashboard while their
          // module route is /client-zone/modules/total-operations).
          const target = new URL(action.nav, url.origin);
          url.pathname = target.pathname;
          url.search = target.search;
        } else {
          const query = action.nav.startsWith('?') ? action.nav.slice(1) : action.nav;
          for (const [key, value] of new URLSearchParams(query)) {
            url.searchParams.set(key, value);
          }
        }
        const href = url.pathname + url.search + url.hash;
        if (options?.navigate) {
          options.navigate(href);
        } else {
          window.history.pushState(window.history.state, '', url.toString());
          // App Router listens to popstate and re-reads the location — this
          // propagates same-page query changes to useSearchParams, which a
          // bare pushState does not.
          window.dispatchEvent(
            new PopStateEvent('popstate', { state: window.history.state }),
          );
        }
        await sleep(POST_NAV_SETTLE_MS);
      }

      if (action.click) {
        const el = document.querySelector<HTMLElement>(toSelector(action.click));
        el?.click();
        await sleep(POST_CLICK_SETTLE_MS);
      }

      if (action.process_action) {
        // Fires the intent even when the consumer isn't mounted — the
        // pending flag bridges the gap to the tab switch that mounts it
        // (the flow's target_tab navigation runs right after launch).
        requestProcessAction(action.process_action, { moduleKey: options?.moduleKey });
        await sleep(POST_CLICK_SETTLE_MS);
      }

      if (action.wait_for) {
        await waitForElement(toSelector(action.wait_for), WAIT_FOR_TIMEOUT_MS);
      }
    } catch {
      // Launch steps are best-effort — a failed action just means the surface
      // never mounted and the re-dispatched open falls back to a page-level
      // provider, matching the pre-launch behaviour.
    }
  }
}
